"use client"

import * as React from "react"
import {
    ColumnDef
} from "@tanstack/react-table"
import { MoreVerticalIcon} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import ExpenseCreate from "./Create"
import ExpenseEdit from "./Edit"
import CommonTableList from "@/components/common/TableList"
import { ExpenseDetails } from "./ExpenseDetails"
import { ExpenseData } from "@/interface/entityInterface"
import { format } from "date-fns"
import CommonList from "@/components/common/List"
import CommonDelete from "@/components/common/Delete"

export const ExpensesList = () => {
    const [open, setOpen] = React.useState(false)
    const [expense, setExpense] = React.useState<ExpenseData | null>(null)

    const handleOpen = (row: ExpenseData) => {
        setExpense(row)
        setOpen(true)
    } 

    const columns = React.useMemo<ColumnDef<any>[]>(() => [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    checked={
                        table.getIsAllPageRowsSelected() ||
                        (table.getIsSomePageRowsSelected() && "indeterminate")
                    }
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label="Select all"
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label="Select row"
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "id",
            header: "ID",
            cell: ({ row }) => (
                <div className="capitalize">{row.getValue("id")}</div>
            ),
        },
        {
            accessorFn: (row) => row?.vehicle.license_plate || "",   
            accessorKey: "vehicle",
            id: "vehicle",
            header: "Vehicle",
            cell: ({ row }) => {
                const vehicle = row.getValue("vehicle") as any;
                return (
                    <div className="flex flex-col text-sm space-y-1">
                        <span className="font-medium">{vehicle}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: "date",
            header: "Date",
            cell: ({ row }) => {
                return (
                    <div className="flex flex-col text-sm space-y-1">
                        <span className="font-medium">{format(row.getValue("date"), "dd MMMM yyyy HH:mm")}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: "odometer",
            header: "Odometer (km)",
            cell: ({ row }) => {
                return (
                    <div className="flex flex-col text-sm space-y-1">
                        <span className="font-medium">{row.getValue("odometer")}</span>
                    </div>
                );
            },
        },
        {
            id: "actions",
            cell: ({row}) => {
                return (
                    <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                            size="icon"
                        >
                            <MoreVerticalIcon />
                            <span className="sr-only">Open menu</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-32">
                        <ExpenseEdit prop={row.original.id} />
                        <DropdownMenuSeparator />
                        <CommonDelete id={row.original.id} resource="expenses" />
                    </DropdownMenuContent>
                </DropdownMenu>
                )
            }
        }
    ], []);

    return (
        <>
            <CommonList>
                <CommonTableList 
                    columns={columns} 
                    filterSearch="odometer" 
                    create={<ExpenseCreate />} 
                    rowClick={handleOpen} 
                />
            </CommonList>
            {open && <ExpenseDetails open={open} setOpen={setOpen} expense={expense} />}
        </>
    )
}

export default ExpensesList;